// account-mfa.js — MFA (TOTP) enroll + disable flow on the
// Account page.
//
// The MFA panel itself (status row + button label / color) is
// rendered by account-auth.js's refreshAccountMfaStatus. This
// module wires the button's click → enroll modal flow, the modal
// internals (QR + secret + verify), and the disable flow (with
// reauth gate).
//
// On successful enrollment, immediately POSTs to /v1/user/mfa/
// recovery-codes and shows the codes modal — users MUST save the
// codes before closing, so they're not locked out if they lose
// the authenticator app.
//
// State: accountMfaState (factorId + challengeId of the in-flight
// enrollment). Cleared after verify or cancel.
//
// Dependencies:
//   getSupabase()        — Supabase client (bridged because supabase
//                          is a `let` in bootstrap.js's closure)
//   requireReauth()      — direct import from reauth.js
//   refreshAccountMfaStatus() — direct import from account-auth.js
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2e); bridge
// call switched to direct import for requireReauth in Tier 2n.
import { $ } from "./dom.js";
import { showToast, showConfirm } from "./ui.js";
import { showRecoveryCodesModal } from "./recovery-codes-modal.js";
import { refreshAccountMfaStatus } from "./account-auth.js";
import { requireReauth } from "./reauth.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

// State for the in-flight MFA enrollment. Cleared after verify or cancel.
const accountMfaState = { factorId: null, challengeId: null };

function closeMfaModal() {
  $("mfa-enroll-backdrop").classList.remove("open");
  $("mfa-qr-loading").style.display = "";
  $("mfa-qr-image").style.display = "none";
  $("mfa-secret-row").style.display = "none";
  $("mfa-code-input").value = "";
  $("mfa-error").textContent = "";
}

async function beginMfaEnroll() {
  const supabase = getSupabase();
  if (!supabase) return;
  $("mfa-enroll-backdrop").classList.add("open");
  $("mfa-qr-loading").style.display = "";
  $("mfa-qr-image").style.display = "none";
  $("mfa-secret-row").style.display = "none";
  $("mfa-code-input").value = "";
  $("mfa-error").textContent = "";
  try {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
    });
    if (error) throw error;
    accountMfaState.factorId = data.id;
    // Supabase returns data.totp.qr_code (SVG data URI) + data.totp.secret.
    $("mfa-qr-image").src = data.totp.qr_code;
    $("mfa-qr-image").style.display = "";
    $("mfa-secret-code").textContent = data.totp.secret;
    $("mfa-secret-row").style.display = "";
    $("mfa-qr-loading").style.display = "none";
    // Create the challenge up front so verify() has something to reference.
    const ch = await supabase.auth.mfa.challenge({ factorId: data.id });
    if (ch.error) throw ch.error;
    accountMfaState.challengeId = ch.data.id;
    setTimeout(() => $("mfa-code-input").focus(), 100);
  } catch (e) {
    $("mfa-qr-loading").style.display = "none";
    $("mfa-error").textContent =
      "Could not start enrollment: " + (e.message || "unknown");
  }
}

// ---- module init: wire MFA button + modal handlers ----

function wireListeners() {
  // Main "Enable / Disable 2FA" button.
  const mfaBtn = $("account-mfa-btn");
  if (mfaBtn) {
    mfaBtn.addEventListener("click", async () => {
      const btn = mfaBtn;
      const mode = btn.dataset.mode;
      if (mode === "disable") {
        const factorId = btn.dataset.factorId;
        if (!factorId) return;
        const ok = await showConfirm(
          "Disable two-factor authentication?",
          "Your account will go back to password-only sign-in. Your recovery codes will also be invalidated. You can re-enable 2FA at any time.",
          "Disable 2FA",
        );
        if (!ok) return;
        // Disabling 2FA is sensitive — require reauth.
        const prevText = btn.textContent;
        btn.disabled = true;
        btn.textContent = "Disabling…";
        try {
          try {
            await requireReauth();
          } catch (e) {
            if (e && e.message === "reauth_cancelled") return;
            throw e;
          }
          const supabase = getSupabase();
          if (!supabase) return;
          const { error } = await supabase.auth.mfa.unenroll({ factorId });
          if (error) throw error;
          showToast("success", "Two-factor authentication disabled.");
          await refreshAccountMfaStatus();
          // Recovery codes row hidden by refreshAccountMfaStatus when no
          // verified factor.
        } catch (e) {
          showToast("error", "Failed to disable 2FA: " + (e.message || "unknown"));
        } finally {
          btn.disabled = false;
          btn.textContent = prevText;
        }
      } else {
        beginMfaEnroll();
      }
    });
  }

  // Cancel button + backdrop click — both go through the same
  // unenroll-then-close path so a half-finished factor doesn't
  // linger as an unverified row in Supabase.
  const cancelBtn = $("mfa-cancel");
  if (cancelBtn) {
    cancelBtn.addEventListener("click", async () => {
      const supabase = getSupabase();
      if (accountMfaState.factorId && supabase) {
        try {
          await supabase.auth.mfa.unenroll({
            factorId: accountMfaState.factorId,
          });
        } catch {}
      }
      accountMfaState.factorId = null;
      accountMfaState.challengeId = null;
      closeMfaModal();
    });
  }
  const backdrop = $("mfa-enroll-backdrop");
  if (backdrop) {
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop && cancelBtn) cancelBtn.click();
    });
  }

  // Verify the 6-digit code.
  const verifyBtn = $("mfa-verify");
  if (verifyBtn) {
    verifyBtn.addEventListener("click", async () => {
      const supabase = getSupabase();
      if (!supabase) return;
      const code = ($("mfa-code-input").value || "").trim();
      const err = $("mfa-error");
      err.textContent = "";
      if (!/^\d{6}$/.test(code)) {
        err.textContent = "Enter the 6-digit code from your authenticator app.";
        return;
      }
      if (!accountMfaState.factorId || !accountMfaState.challengeId) {
        err.textContent = "Enrollment state expired. Close this and try again.";
        return;
      }
      const btn = verifyBtn;
      const prev = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Verifying…";
      try {
        const { error } = await supabase.auth.mfa.verify({
          factorId: accountMfaState.factorId,
          challengeId: accountMfaState.challengeId,
          code,
        });
        if (error) throw error;
        accountMfaState.factorId = null;
        accountMfaState.challengeId = null;
        closeMfaModal();
        // Immediately generate recovery codes — user MUST save before
        // closing the modal, so if they lose their authenticator they
        // aren't permanently locked out.
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) throw new Error("Not signed in.");
          const r = await fetch(COORDINATOR + "/v1/user/mfa/recovery-codes", {
            method: "POST",
            headers: { Authorization: "Bearer " + session.access_token },
          });
          if (!r.ok) {
            const e = await r.json().catch(() => ({}));
            throw new Error(
              e.message || e.error || "Could not generate recovery codes.",
            );
          }
          const body = await r.json();
          await showRecoveryCodesModal(body.codes || []);
        } catch (recErr) {
          // 2FA IS enabled at this point — the verify succeeded. We
          // can't let the user walk away without recovery codes
          // though; show an error directing them to regenerate from
          // the Account page.
          showToast(
            "error",
            "2FA enabled but failed to generate recovery codes: " +
              (recErr.message || "unknown") +
              ". Use the Regenerate button on the Account page.",
          );
        }
        showToast("success", "Two-factor authentication enabled.");
        await refreshAccountMfaStatus();
      } catch (e) {
        err.textContent = e.message || "Code didn’t verify.";
      } finally {
        btn.disabled = false;
        btn.textContent = prev;
      }
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
